import { ActionTree, ActionContext } from 'vuex';
import { RootState, useStore } from '@/store';
import { State } from './state';
import { Mutations } from './mutations';
import { GeneralMutationTypes } from './mutation-types';
import { GeneralActionTypes } from './action-types';
import {
  EtlInfoApiResponse,
  GeneralApiOptions,
  LeadDataResolutionMap,
  UserHierarchyApiResponse,
  UserPreferencesResponse,
} from './types';

import ServerAPI, {
  combineQueryParams,
  handleError,
} from '@/services/serverAPI';
import { getIntegration } from '@/services/config';
import axios from 'axios';
import { authState } from '@/services/integrations/auth0';
import { Banker } from '../leads/types';

type AugmentedAction = {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1]
  ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<State, RootState>, 'commit'>;

export interface Actions {
  [GeneralActionTypes.GET_USER_HIERARCHY](
    { commit }: AugmentedAction,
    payload: GeneralApiOptions
  ): Promise<void>;
  [GeneralActionTypes.GET_USER_PREFERENCES]({
    commit,
  }: AugmentedAction): Promise<void>;
  [GeneralActionTypes.GET_META_DATA]({
    commit,
  }: AugmentedAction): Promise<void>;
  [GeneralActionTypes.GET_ETL_DATA]({ commit }: AugmentedAction): Promise<void>;
  [GeneralActionTypes.SET_SHOW_SESSION_MODAL](
    { commit }: AugmentedAction,
    showSessionModal: boolean
  ): Promise<void>;
  [GeneralActionTypes.SET_USER_PREFERENCES](
    { commit }: AugmentedAction,
    viewHistoricPricing: boolean
  ): Promise<void>;
  [GeneralActionTypes.GET_CLIENT_NAME_RESOLUTION_MAP]({
    commit,
  }: AugmentedAction): Promise<void>;
  [GeneralActionTypes.GET_CONTAINER_NAME_RESOLUTION_MAP](
    { commit }: AugmentedAction,
    ids: string[]
  ): Promise<void>;
  [GeneralActionTypes.GET_COLLECTION_NAME_RESOLUTION_MAP](
    { commit }: AugmentedAction,
    ids: string[]
  ): Promise<void>;
  [GeneralActionTypes.GET_LEAD_DATA_RESOLUTION_MAP](
    { commit }: AugmentedAction,
    payload: {
      ids: string[];
      fetchAllIds: boolean;
    }
  ): Promise<void>;
  [GeneralActionTypes.GET_BANKER_NAME_RESOLUTION_MAP]({
    commit,
  }: AugmentedAction): Promise<void>;
  [GeneralActionTypes.RESET_COLLECTION_CONTAINER_NAME_RESOLUTION]({
    commit,
  }: AugmentedAction): Promise<void>;
}

export const actions: ActionTree<State, RootState> & Actions = {
  async [GeneralActionTypes.GET_USER_HIERARCHY](
    { commit },
    filterOptions: GeneralApiOptions
  ) {
    try {
      const paramString = combineQueryParams({
        ['filter_field']: filterOptions.portfolioFilterField,
        ['filter_id']: filterOptions.portfolioFilterId,
      });

      const userHierarchy: UserHierarchyApiResponse = await ServerAPI.get(
        `/hierarchy${paramString}`
      );
      commit(GeneralMutationTypes.SET_USER_HIERARCHY, {
        userHierarchy: userHierarchy as UserHierarchyApiResponse,
      });
    } catch (e) {
      handleError(e);
      commit(GeneralMutationTypes.SET_USER_HIERARCHY, {
        userHierarchy: {} as UserHierarchyApiResponse,
      });
    }
  },
  async [GeneralActionTypes.GET_USER_PREFERENCES]({ commit }) {
    try {
      const userPreferences: UserPreferencesResponse = await ServerAPI.get(
        `/users/preferences`
      );
      if (userPreferences) {
        commit(GeneralMutationTypes.SET_USER_PREFERENCES, {
          userPreferences: userPreferences as UserPreferencesResponse,
        });
      }
    } catch (e) {
      handleError(e);
      commit(GeneralMutationTypes.SET_USER_PREFERENCES, {
        userPreferences: {} as UserPreferencesResponse,
      });
    }
  },
  async [GeneralActionTypes.SET_USER_PREFERENCES](
    { commit },
    viewHistoricPricing
  ) {
    try {
      const userPreferences: UserPreferencesResponse = await ServerAPI.patch(
        `/users/preferences`,
        {
          viewHistoricPricing: viewHistoricPricing,
        }
      );
      if (userPreferences) {
        commit(GeneralMutationTypes.SET_USER_PREFERENCES, {
          userPreferences: userPreferences as UserPreferencesResponse,
        });
      }
    } catch (e) {
      handleError(e);
    }
  },
  async [GeneralActionTypes.GET_META_DATA]({ commit }) {
    try {
      const metaCountries = ServerAPI.get(`/meta/countries`);
      const metaMarkets = ServerAPI.get(`/meta/markets`);
      const metaTeams = ServerAPI.get(`/meta/teams`);

      const responses = await Promise.all([
        metaCountries,
        metaMarkets,
        metaTeams,
      ]);

      commit(GeneralMutationTypes.SET_META_DATA, {
        metaCountries: responses[0],
        metaMarkets: responses[1],
        metaTeams: responses[2],
      });
    } catch (e) {
      handleError(e);
    }
  },
  async [GeneralActionTypes.GET_ETL_DATA]({ commit }) {
    try {
      const store = useStore();
      if (!store.state.general.etl.updateDate) {
        const etlInfo: EtlInfoApiResponse = await ServerAPI.get(
          `/meta/get_etl_info`
        );
        commit(GeneralMutationTypes.SET_ETL_DATA, { etlInfo: etlInfo });
      }
    } catch (e) {
      handleError(e);
      commit(GeneralMutationTypes.SET_ETL_DATA, {
        etlInfo: {} as EtlInfoApiResponse,
      });
    }
  },
  async [GeneralActionTypes.SET_SHOW_SESSION_MODAL](
    { commit },
    showSessionModal: boolean
  ) {
    commit(GeneralMutationTypes.SET_SHOW_SESSION_MODAL, { showSessionModal });
  },
  async [GeneralActionTypes.GET_CLIENT_NAME_RESOLUTION_MAP]({ commit }) {
    commit(GeneralMutationTypes.SET_CLIENT_NAME_RESOLUTION_LOADING, true);
    try {
      const customNameResolution = getIntegration('customNameResolution');

      if (customNameResolution) {
        const allIdsInScopeOfUser: string[] = await ServerAPI.get(
          `/meta/clients`
        );
        const nameResolutionMap = await axios.post<{ [key: string]: string }>(
          `${customNameResolution.domain}/clients`,
          { ids: allIdsInScopeOfUser },
          {
            headers: {
              Authorization: `Bearer ${authState.bearerToken}`,
            },
          }
        );
        commit(
          GeneralMutationTypes.SET_CLIENT_NAME_RESOLUTION_MAP,
          nameResolutionMap.data
        );
      }
    } catch (e) {
      commit(GeneralMutationTypes.SET_CLIENT_NAME_RESOLUTION_MAP, {});
    } finally {
      commit(GeneralMutationTypes.SET_CLIENT_NAME_RESOLUTION_LOADING, false);
    }
  },
  async [GeneralActionTypes.GET_CONTAINER_NAME_RESOLUTION_MAP](
    { commit },
    ids
  ) {
    commit(GeneralMutationTypes.SET_CONTAINER_NAME_RESOLUTION_LOADING, true);
    try {
      const customNameResolution = getIntegration('customNameResolution');

      if (customNameResolution) {
        const nameResolutionMap = await axios.post<{ [key: string]: string }>(
          `${customNameResolution.domain}/containers`,
          { ids },
          {
            headers: {
              Authorization: `Bearer ${authState.bearerToken}`,
            },
          }
        );
        commit(
          GeneralMutationTypes.SET_CONTAINER_NAME_RESOLUTION_MAP,
          nameResolutionMap.data
        );
      }
    } catch (e) {
      commit(GeneralMutationTypes.SET_CONTAINER_NAME_RESOLUTION_MAP, {});
    } finally {
      commit(GeneralMutationTypes.SET_CONTAINER_NAME_RESOLUTION_LOADING, false);
    }
  },
  async [GeneralActionTypes.GET_COLLECTION_NAME_RESOLUTION_MAP](
    { commit },
    ids
  ) {
    commit(GeneralMutationTypes.SET_COLLECTION_NAME_RESOLUTION_LOADING, true);
    try {
      const customNameResolution = getIntegration('customNameResolution');

      if (customNameResolution) {
        const nameResolutionMap = await axios.post<{ [key: string]: string }>(
          `${customNameResolution.domain}/collections`,
          { ids },
          {
            headers: {
              Authorization: `Bearer ${authState.bearerToken}`,
            },
          }
        );
        commit(
          GeneralMutationTypes.SET_COLLECTION_NAME_RESOLUTION_MAP,
          nameResolutionMap.data
        );
      }
    } catch (e) {
      commit(GeneralMutationTypes.SET_COLLECTION_NAME_RESOLUTION_MAP, {});
    } finally {
      commit(
        GeneralMutationTypes.SET_COLLECTION_NAME_RESOLUTION_LOADING,
        false
      );
    }
  },
  async [GeneralActionTypes.GET_LEAD_DATA_RESOLUTION_MAP]({ commit }, payload) {
    commit(GeneralMutationTypes.SET_LEAD_DATA_RESOLUTION_LOADING, true);
    try {
      const customNameResolution = getIntegration('customNameResolution');

      if (customNameResolution) {
        let idsToFetch = payload.ids;

        if (payload.fetchAllIds) {
          const allIdsInScopeOfUser: string[] = await ServerAPI.get(
            `/leads/lead_ids`
          );
          idsToFetch = allIdsInScopeOfUser;
        }

        const leadDataResolutionMap = await axios.post<LeadDataResolutionMap>(
          `${customNameResolution.domain}/leads`,
          { ids: idsToFetch },
          {
            headers: {
              Authorization: `Bearer ${authState.bearerToken}`,
            },
          }
        );
        commit(
          GeneralMutationTypes.SET_LEAD_DATA_RESOLUTION_MAP,
          leadDataResolutionMap.data
        );
      }
    } catch (e) {
      commit(GeneralMutationTypes.SET_LEAD_DATA_RESOLUTION_MAP, {});
    } finally {
      commit(GeneralMutationTypes.SET_LEAD_DATA_RESOLUTION_LOADING, false);
    }
  },
  async [GeneralActionTypes.GET_BANKER_NAME_RESOLUTION_MAP]({ commit }) {
    commit(GeneralMutationTypes.SET_BANKER_NAME_RESOLUTION_LOADING, true);
    try {
      const bankers: Array<Banker> = await ServerAPI.get('/meta/bankers');
      commit(GeneralMutationTypes.SET_BANKER_NAME_RESOLUTION_MAP, bankers);
    } catch (e) {
      handleError(e);
      commit(GeneralMutationTypes.SET_BANKER_NAME_RESOLUTION_MAP, []);
    } finally {
      commit(GeneralMutationTypes.SET_BANKER_NAME_RESOLUTION_LOADING, false);
    }
  },
  async [GeneralActionTypes.RESET_COLLECTION_CONTAINER_NAME_RESOLUTION]({
    commit,
  }) {
    commit(
      GeneralMutationTypes.RESET_COLLECTION_CONTAINER_NAME_RESOLUTION,
      undefined
    );
  },
};
